/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { OrderStatusRefinerOptionItem } from '../common/refiner-option';

/**
 * Gets the order status refiner options.
 * @returns - List of refiner options.
 */
const getOrderStatusRefinerOptions = () => async (ctx: IActionContext): Promise<OrderStatusRefinerOptionItem[]> => {
    return Promise.resolve([
        OrderStatusRefinerOptionItem.Created,
        OrderStatusRefinerOptionItem.Invoiced,
        OrderStatusRefinerOptionItem.Canceled
    ]);
};

/**
 * Action input.
 */
export class GetOrderStatusRefinerOptionsInput implements IActionInput {
    public getCacheKey = () => 'OrderHistoryWithSearchAndFiltersOrderStatusRefinerOptions';

    public getCacheObjectType = () => 'OrderHistoryWithSearchAndFiltersOrderStatusRefinerOptions';

    public dataCacheType = (): CacheType => 'instance';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData - Action Data Input.
 * @returns - Input object.
 */
const createGetOrderStatusRefinerOptionsInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    return new GetOrderStatusRefinerOptionsInput();
};

/**
 * Get action.
 * @param input - Input data.
 * @param context - Action context.
 * @returns - List of refiner options.
 */
export async function getOrderStatusRefinerOptionsAction(
    input: GetOrderStatusRefinerOptionsInput,
    context: IActionContext
): Promise<OrderStatusRefinerOptionItem[]> {
    if (!context) {
        throw new Error('getOrderStatusRefinerOptionsAction - Action context cannot be null/undefined');
    }

    let refinerOptions: OrderStatusRefinerOptionItem[] = [];

    try {
        refinerOptions = await getOrderStatusRefinerOptions()(context);
    } catch (error) {
        throw error;
    }

    return refinerOptions;
}

/**
 * Get order status refiner options action.
 * @param id - Unique Id for data action.
 * @param context - Action context.
 * @param input - Input for data action.
 * @returns Api response.
 */
export const getOrderStatusRefinerOptionsActionDataAction = createObservableDataAction<OrderStatusRefinerOptionItem[]>({
    id: '@msdyn365-commerce-modules/order-history-with-search-and-filters/get-order-status-refiner-options',
    action: getOrderStatusRefinerOptionsAction as IAction<OrderStatusRefinerOptionItem[]>,
    input: createGetOrderStatusRefinerOptionsInput
});

export default getOrderStatusRefinerOptionsActionDataAction;
